import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_REF = "ab_first_landing";
const STORAGE_GPS = "ab_gps";               // localStorage: cache GPS coords + reverse-geocoded city (24h TTL)
const STORAGE_GPS_DENIED = "ab_gps_denied"; // localStorage: remember refusal (6h TTL)
const GPS_TTL_MS = 24 * 60 * 60 * 1000;
const GPS_DENIED_TTL_MS = 6 * 60 * 60 * 1000;

const readWithTtl = (key, ttlMs) => {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const obj = JSON.parse(raw);
        if (!obj || typeof obj !== "object") return null;
        if (Date.now() - (obj._t || 0) > ttlMs) {
            localStorage.removeItem(key);
            return null;
        }
        return obj.data;
    } catch {
        return null;
    }
};
const writeWithTtl = (key, data) => {
    try { localStorage.setItem(key, JSON.stringify({ _t: Date.now(), data })); } catch { /* empty */ }
};

const extractKeywordFromReferer = (refUrl) => {
    if (!refUrl) return "";
    try {
        const u = new URL(refUrl);
        const host = u.hostname.toLowerCase();
        const params = u.searchParams;
        if (host.includes("bing.")) return params.get("q") || "";
        if (host.includes("yahoo.")) return params.get("p") || params.get("q") || "";
        if (host.includes("duckduckgo.")) return params.get("q") || "";
        if (host.includes("qwant.")) return params.get("q") || "";
        if (host.includes("ecosia.")) return params.get("q") || "";
        if (host.includes("yandex.")) return params.get("text") || "";
        if (host.includes("startpage.")) return params.get("query") || params.get("q") || "";
        return "";
    } catch {
        return "";
    }
};

// Ask the browser for GPS coordinates, then reverse-geocode via OpenStreetMap Nominatim (free, no key).
const requestGpsOnce = () => {
    return new Promise((resolve) => {
        const cached = readWithTtl(STORAGE_GPS, GPS_TTL_MS);
        if (cached) return resolve(cached);
        if (readWithTtl(STORAGE_GPS_DENIED, GPS_DENIED_TTL_MS)) return resolve(null);
        if (!("geolocation" in navigator)) return resolve(null);

        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                const lat = pos.coords.latitude;
                const lon = pos.coords.longitude;
                let city = "";
                let postal = "";
                try {
                    const r = await fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lon}&zoom=14&addressdetails=1`, {
                        headers: { "Accept-Language": "fr" },
                    });
                    if (r.ok) {
                        const data = await r.json();
                        const a = data.address || {};
                        city = a.city || a.town || a.village || a.municipality || a.suburb || "";
                        postal = a.postcode || "";
                    }
                } catch { /* network failure — keep coords only */ }
                const out = { gps_lat: lat, gps_lon: lon, gps_city: city, gps_postal: postal };
                writeWithTtl(STORAGE_GPS, out);
                resolve(out);
            },
            () => {
                writeWithTtl(STORAGE_GPS_DENIED, true);
                resolve(null);
            },
            { enableHighAccuracy: false, timeout: 15000, maximumAge: 600000 }
        );
    });
};

const VisitTracker = () => {
    const { pathname, search } = useLocation();

    useEffect(() => {
        if (pathname.startsWith("/admin-")) return;

        const params = new URLSearchParams(search);
        const kwFromUrl =
            params.get("kw") ||
            params.get("keyword") ||
            params.get("utm_term") ||
            params.get("search_term") ||
            "";
        const kwFromRef = extractKeywordFromReferer(document.referrer);
        const search_keyword = (kwFromUrl || kwFromRef || "").slice(0, 300);

        let originalRef = sessionStorage.getItem(STORAGE_REF);
        if (!originalRef) {
            originalRef = document.referrer || "";
            sessionStorage.setItem(STORAGE_REF, originalRef);
        }

        (async () => {
            const gps = await requestGpsOnce();
            const payload = {
                path: pathname,
                referer: originalRef,
                utm_source: params.get("utm_source") || "",
                utm_medium: params.get("utm_medium") || "",
                utm_campaign: params.get("utm_campaign") || "",
                utm_term: params.get("utm_term") || "",
                gclid: params.get("gclid") || "",
                search_keyword,
                ...(gps || {}),
            };
            axios.post(`${API}/track-visit`, payload).catch(() => { /* silent */ });
        })();
    }, [pathname, search]);

    return null;
};

export default VisitTracker;
